import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { MessageCircle, Paperclip, Calendar, CheckSquare } from "lucide-react";
import { Task2 } from "@/types/tasks";
import { getIniciais } from "@/services/profilesCache";

interface TaskCardProps {
  task: Task2;
  onClick: () => void;
  columnColor?: string;
}

export function TaskCard({ task, onClick, columnColor }: TaskCardProps) {
  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData("taskId", task.id);
  };

  const getBorderStyle = () => {
    if (columnColor) {
      return { borderLeftColor: columnColor };
    }
    return {};
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: '2-digit'
    });
  };

  const completedChecklist = task.checklist.filter(item => item.completed).length;

  return (
    <Card 
      className="cursor-pointer hover:shadow-md transition-all border-l-4 group"
      style={getBorderStyle()}
      onClick={onClick}
      draggable
      onDragStart={handleDragStart}
    >
      <CardContent className="p-2">
        <div className="space-y-1">
          {/* Project Badge */}
          <Badge variant="outline" className="text-xs text-muted-foreground px-1 py-0 h-4">
            {task.projectName.split(' – ')[0]}
          </Badge>
          
          {/* Title */}
          <div>
            <h4 className="font-medium text-xs line-clamp-2 group-hover:text-primary transition-colors">
              {task.title}
            </h4>
            {task.description && (
              <p className="text-xs text-muted-foreground line-clamp-1 mt-1">
                {task.description}
              </p>
            )}
          </div>
          
          {/* Metadata */}
          <div className="space-y-0.5">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground truncate">{task.setor}</span>
              <Badge variant="outline" className="text-xs px-1 py-0 h-4">
                {task.tipo}
              </Badge>
            </div>
            
            {task.endDate && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Calendar className="w-3 h-3" />
                <span>até {formatDate(task.endDate)}</span>
              </div>
            )}
          </div>
          
          {/* Assignees */}
          {task.assignees.length > 0 && (
            <div className="flex items-center gap-1">
              <div className="flex -space-x-1">
                {task.assignees.slice(0, 2).map((assignee) => (
                  <Avatar key={assignee.id} className="w-5 h-5 border border-background">
                    <AvatarImage src={assignee.avatar} />
                    <AvatarFallback className="text-xs">
                      {getIniciais(assignee.name)}
                    </AvatarFallback>
                  </Avatar>
                ))}
                {task.assignees.length > 2 && (
                  <div className="w-5 h-5 rounded-full bg-muted border border-background flex items-center justify-center">
                    <span className="text-xs text-muted-foreground">+{task.assignees.length - 2}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Footer */}
          <div className="flex items-center justify-between pt-0.5">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {task.comments.length > 0 && (
                <div className="flex items-center gap-1">
                  <MessageCircle className="w-3 h-3" />
                  <span>{task.comments.length}</span>
                </div>
              )}
              {task.attachments.length > 0 && (
                <div className="flex items-center gap-1">
                  <Paperclip className="w-3 h-3" />
                  <span>{task.attachments.length}</span>
                </div>
              )}
              {task.checklist.length > 0 && (
                <div className="flex items-center gap-1">
                  <CheckSquare className="w-3 h-3" />
                  <span>{completedChecklist}/{task.checklist.length}</span>
                </div>
              )}
            </div>
            
            {/* Priority indicator */}
            <div className={`w-1.5 h-1.5 rounded-full ${
              task.priority === 'high' ? 'bg-red-500' :
              task.priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
            }`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}